import { useCallback, useState, type ReactNode } from 'react';
import { Badge } from '@/shared/ui/badge';
import { AppList } from '@/shared/app-list-layout';
import type { DataTableView } from '@/shared/app-list-layout';
import type { ListColumn } from '@/shared/app-list-layout/types';
import { getWorkspaceScope } from '@/app/workspace/store';
import type { BadgeTone } from '@/shared/ui/badge';
import { MediaApi } from '@/modules/media/services/api';
import type { MediaAsset, MediaAssetStatus } from '@/api/schemas/media';
import { UploadControl } from './parts/upload-control';

/**
 * The tenant's media library.
 *
 * The rows come from M44's list read; the toolbar carries the upload control.
 * The asset's OWNER is the tenant the session resolved — the console does not
 * choose an owner, and the server refuses an owner outside the tenant anyway.
 */

const STATUS_TONE: Record<MediaAssetStatus, { tone: BadgeTone; label: string }> = {
  awaiting_upload: { tone: 'caution', label: 'Awaiting upload' },
  scan_pending: { tone: 'caution', label: 'Awaiting scan' },
  available: { tone: 'positive', label: 'Available' },
  quarantined: { tone: 'critical', label: 'Quarantined' },
  scan_failed: { tone: 'critical', label: 'Scan failed' },
  upload_expired: { tone: 'neutral', label: 'Upload expired' },
};

/** Status options surfaced in the column-bound filter dropdown. */
const STATUS_OPTIONS = (Object.keys(STATUS_TONE) as MediaAssetStatus[]).map((status) => ({
  value: status,
  label: STATUS_TONE[status].label,
}));

/**
 * Saved views rendered as tabs above the library.
 *
 * The tabs are the states an operator actually acts on: what is ready, what is
 * still settling, and what the scanner refused. The two "waiting" states are
 * merged into one tab because they are the same job — come back later.
 */
function buildMediaViews(counts: Partial<Record<string, number>>): DataTableView[] {
  return [
    { id: 'all', label: 'All media', count: counts.all },
    { id: 'available', label: 'Available', filters: { status: 'available' }, count: counts.available },
    {
      id: 'pending',
      label: 'Processing',
      filters: { status: 'scan_pending' },
      count: (counts.scan_pending ?? 0) + (counts.awaiting_upload ?? 0),
    },
    {
      id: 'quarantined',
      label: 'Quarantined',
      filters: { status: 'quarantined' },
      count: counts.quarantined,
    },
    {
      id: 'scan_failed',
      label: 'Scan failed',
      filters: { status: 'scan_failed' },
      count: counts.scan_failed,
    },
  ];
}

/**
 * The fields a search term is matched against.
 *
 * The status is matched by its LABEL, not its wire value: a user searching for
 * "quarantined" is looking for the word they can see on the row.
 */
function searchFields(row: MediaAsset): ReadonlyArray<string | null | undefined> {
  return [row.mimeType, STATUS_TONE[row.status].label, row.contentHashSha256];
}

const COLUMNS: ReadonlyArray<ListColumn<MediaAsset>> = [
  {
    id: 'mimeType',
    header: 'Type',
    primary: true,
    sortable: true,
    cell: (row) => <span className="font-medium text-content">{row.mimeType}</span>,
  },
  {
    id: 'byteSize',
    header: 'Size',
    sortable: true,
    cell: (row) => <span>{row.byteSize === null ? '—' : `${row.byteSize} B`}</span>,
  },
  {
    id: 'status',
    header: 'Status',
    sortable: true,
    filter: { type: 'select', options: STATUS_OPTIONS, placeholder: 'Any' },
    cell: (row) => {
      const { tone, label } = STATUS_TONE[row.status];
      return <Badge tone={tone}>{label}</Badge>;
    },
  },
  {
    id: 'contentHashSha256',
    header: 'Digest',
    cell: (row) => (
      <span className="font-mono text-xs text-content-muted">
        {row.contentHashSha256 === null ? '—' : row.contentHashSha256.slice(0, 12)}
      </span>
    ),
  },
];

export function ListPage(): ReactNode {
  const read = useCallback(() => MediaApi.listAssets(), []);
  const ownerId = getWorkspaceScope()?.tenant.id;
  // The surface owns the re-read; the upload control needs it, so it is captured
  // here rather than duplicated as a second read path.
  const [reload, setReload] = useState<(() => void) | null>(null);

  return (
    <AppList<MediaAsset>
      title="Media"
      description="Every asset this tenant owns. Uploads land in the object store; the API settles what it finds there."
      read={read}
      searchFields={searchFields}
      countKeyOf={(row) => row.status}
      buildViews={buildMediaViews}
      columns={COLUMNS}
      getRowId={(row) => row.mediaAssetId}
      // Four columns fit a phone, but the digest does not, so the page asks for
      // the responsive presentation: cards below `sm`, the table above.
      responsive
      onReloadReady={setReload}
      toolbarActions={
        ownerId === undefined || reload === null ? undefined : (
          <UploadControl ownerId={ownerId} onUploaded={reload} />
        )
      }
      emptyTitle="No media yet"
      emptyDescription="Upload a file to create this tenant's first asset."
      noMatchDescription="No asset matches this tab, search or filter."
    />
  );
}
